import { DialogTitle } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import { UPDATE_ARCHIVED } from '../../constants/career-history'
import { useNotificationContext, usePartnerContext } from '../../context'
import { mutation } from '../../graphql'
import {
  getSampleDataErrorKey,
  isSampleCareer,
  partitionBySample
} from '../../helpers/sample-data'
import { useCreateLog } from '../../hooks/use-create-log'
import { useRemoveSampleData } from '../../hooks/use-sample-data'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'

const ArchiveCareers = ({
  careers,
  canDelete,
  updateHandler,
  open,
  setOpen
}) => {
  const { t } = useTranslation()
  const { success, error } = useNotificationContext()
  const { partner } = usePartnerContext()

  const queryClient = useQueryClient()
  const { createCareerHistoryLog } = useCreateLog()
  const { mutateAsync: removeSampleData } = useRemoveSampleData()

  const [isLoading, setIsLoading] = useState(false)

  // Mit Löschrecht werden Beispielstellen samt Beispieltalenten entfernt statt
  // archiviert. Ohne bleibt es beim Archivieren, das ist umkehrbar, und
  // archivierte Beispielstellen erkennt der Hinweis weiterhin.
  const { sample, real } = canDelete
    ? partitionBySample(careers, isSampleCareer)
    : { sample: [], real: careers }

  const archiveCareers = async () => {
    setIsLoading(true)

    const archiveCareer = ({ id: career_id }) =>
      Promise.all([
        mutation({
          mutation: 'updateCareer',
          input: { id: career_id, archived: true }
        }),
        createCareerHistoryLog({
          career_id,
          context: UPDATE_ARCHIVED,
          input: { archived: true }
        })
      ])

    try {
      await Promise.all(real.map(archiveCareer))
    } catch {
      setIsLoading(false)
      return
    }

    let kept = []
    let sampleFailed = false

    if (sample.length) {
      try {
        const result = await removeSampleData({
          scope: { career_ids: sample.map(({ id }) => id) },
          source: 'archive_bulk'
        })

        // Beispielstellen mit echten Talenten sind jetzt normale Stellen. Sie
        // werden archiviert, wie in der Auswahl gewünscht.
        kept = result.kept_careers
        await Promise.all(kept.map(archiveCareer))
      } catch (err) {
        // Die echten Stellen sind schon archiviert, deshalb trotzdem schließen.
        // Ein neuer Versuch geht über den Hinweis über der Liste.
        sampleFailed = true
        const errorKey = getSampleDataErrorKey(err)
        if (errorKey) error(t(errorKey))
      }
    }

    await queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.careersByPartner(partner.id)
    })

    updateHandler()
    if (!sampleFailed) {
      success(
        real.length || kept.length
          ? t('careers.archive_bulk.success')
          : t('sample_data.remove.success')
      )
    }
    setIsLoading(false)
    setOpen(false)
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='lg'>
      <DialogTitle className='text-center text-2xl font-medium leading-6 text-gray-900'>
        {t('careers.archive_bulk.modal_title')}
      </DialogTitle>
      <span className='mt-2 block text-center text-base text-gray-600'>
        {t('careers.archive_bulk.modal_description')}
      </span>
      {sample.length > 0 && (
        <p className='mt-3 text-center text-sm text-gray-600'>
          {t('sample_data.archive_careers.mixed_hint', {
            count: sample.length
          })}
        </p>
      )}
      <div className='mt-8 flex flex-row gap-3'>
        <Button.SecondaryLG
          fullWidth
          text={t('careers.archive_bulk.cancel_action')}
          onClick={() => setOpen(false)}
        />
        <Button.WarningLG
          fullWidth
          text={
            careers.length === 1
              ? t('careers.archive_bulk.confirm_single', {
                  count: careers.length
                })
              : t('careers.archive_bulk.confirm_multiple', {
                  count: careers.length
                })
          }
          onClick={archiveCareers}
          isLoading={isLoading}
        />
      </div>
    </Modal>
  )
}

ArchiveCareers.propTypes = {
  careers: PropTypes.array.isRequired,
  // Löschrecht für Beispieldaten (useSampleDataAccess().canRemove)
  canDelete: PropTypes.bool,
  updateHandler: PropTypes.func,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

ArchiveCareers.defaultProps = {
  canDelete: false,
  updateHandler: () => undefined
}

export default ArchiveCareers
